/**
 * <jc-header-search>: the wide search bar in the header. Clicking or typing in
 * it opens Horizon's search modal (#search-modal), the same one the live theme
 * uses, so shoppers get its recently viewed items and product recommendations
 * before typing, and predictive results as they type. Whatever was already in
 * the bar is carried into the modal's input. The bar's own form (search page)
 * is only used if the modal is unavailable.
 */
class JcHeaderSearch extends HTMLElement {
  /** @type {AbortController | undefined} */
  #listeners;

  connectedCallback() {
    this.input = /** @type {HTMLInputElement | null} */ (this.querySelector('.jc-search__input'));
    if (!this.input) return;

    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    this.input.addEventListener('pointerdown', (event) => {
      if (this.#open()) event.preventDefault();
    }, { signal });
    this.input.addEventListener('focus', () => this.#open(), { signal });
    this.querySelector('form')?.addEventListener('submit', (event) => {
      if (this.#open()) event.preventDefault();
    }, { signal });
    this.input.addEventListener('keydown', (event) => {
      // A typed character goes to the modal input along with what's there.
      if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        if (this.#open(this.input.value + event.key)) event.preventDefault();
      }
    }, { signal });
  }

  disconnectedCallback() {
    this.#listeners?.abort();
  }

  /**
   * Opens the search modal, optionally with text, and moves focus to it.
   * @param {string} [text]
   * @returns {boolean} whether the modal was opened
   */
  #open(text = this.input?.value ?? '') {
    const modal = /** @type {any} */ (document.getElementById('search-modal'));
    if (!modal || typeof modal.showDialog !== 'function') return false;
    if (modal.querySelector('dialog')?.open) return true;

    this.input?.blur();
    modal.showDialog();

    const modalInput = /** @type {HTMLInputElement | null} */ (modal.querySelector('input[name="q"]'));
    if (modalInput) {
      if (text && modalInput.value !== text) {
        modalInput.value = text;
        modalInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
      requestAnimationFrame(() => {
        modalInput.focus();
        const end = modalInput.value.length;
        modalInput.setSelectionRange(end, end);
      });
    }
    return true;
  }
}

if (!customElements.get('jc-header-search')) {
  customElements.define('jc-header-search', JcHeaderSearch);
}
